<template>
  <div class="order-detail-page">
    <div class="mobile-status-padding"></div>
    <!-- Load order detail on mount -->
    <VBusHelper
      :emitOnMounted="{
        event: $constants.EVENTS.ORDERS.DETAIL,
        payload: { id: orderId }
      }"
    />

    <!-- Loading State -->
    <div v-if="$stores.orders.state.detail.loading" class="loading">
      <div class="spinner"></div>
      <p>Chargement de la commande...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="$stores.orders.state.detail.error" class="error">
      <p>{{ $stores.orders.state.detail.error }}</p>
      <button @click="retry" class="retry-button">Réessayer</button>
    </div>

    <!-- Order Detail -->
    <div v-else-if="order" class="order-detail">
      <!-- Header -->
      <div class="detail-header">
        <button @click="goBack" class="back-button">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              d="M19 12H5M12 19l-7-7 7-7"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Retour
        </button>
        <h1>Commande #{{ order.orderNumber }}</h1>
        <span class="order-status" :class="getStatusClass(order.status)">
          {{ getStatusLabel(order.status) }}
        </span>
      </div>

      <!-- Status Timeline -->
      <div class="status-timeline">
        <div
          v-for="(step, index) in statusSteps"
          :key="index"
          class="timeline-step"
          :class="{
            active: isStepActive(step.status),
            completed: isStepCompleted(step.status)
          }"
        >
          <div class="step-icon">
            <svg
              v-if="isStepCompleted(step.status)"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path
                d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
              />
            </svg>
            <div v-else class="step-dot"></div>
          </div>
          <div class="step-info">
            <span class="step-label">{{ step.label }}</span>
            <span v-if="getStepTime(step.status)" class="step-time">{{
              getStepTime(step.status)
            }}</span>
          </div>
          <div v-if="index < statusSteps.length - 1" class="step-line"></div>
        </div>
      </div>

      <!-- Restaurant Info -->
      <div class="section restaurant-info">
        <h2>Restaurant</h2>
        <div
          class="restaurant-card"
          @click="goToRestaurant(order.restaurant.id)"
        >
          <ImgComponent
            :src="order.restaurant.image"
            :alt="order.restaurant.name"
            class="restaurant-image"
          />
          <div class="restaurant-details">
            <h3 class="restaurant-name">{{ order.restaurant.name }}</h3>
            <p class="restaurant-address">
              {{ order.restaurant.address?.city }}
            </p>
          </div>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            class="arrow-icon"
          >
            <path
              d="M9 18l6-6-6-6"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </div>

      <!-- Order Items -->
      <div class="section order-items">
        <h2>Articles</h2>
        <div class="items-list">
          <div v-for="item in order.items" :key="item.id" class="order-item">
            <div class="item-info">
              <span class="item-quantity">{{ item.quantity }}x</span>
              <div class="item-details">
                <h4 class="item-name">{{ item.dishName }}</h4>
                <p v-if="item.options?.length" class="item-options">
                  {{ item.options.map((opt) => opt.name).join(', ') }}
                </p>
              </div>
            </div>
            <span class="item-price">{{ formatPrice(item.totalPrice) }}</span>
          </div>
        </div>
      </div>

      <!-- Delivery Info -->
      <div class="section delivery-info">
        <h2>Livraison</h2>
        <div class="delivery-details">
          <div class="delivery-row">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              class="icon"
            >
              <path
                d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <circle cx="12" cy="10" r="3" stroke-width="2" />
            </svg>
            <span
              >{{ order.delivery.address?.street }},
              {{ order.delivery.address?.city }}</span
            >
          </div>
          <div class="delivery-row">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              class="icon"
            >
              <circle cx="12" cy="12" r="10" stroke-width="2" />
              <polyline
                points="12 6 12 12 16 14"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>Temps estimé: {{ order.delivery.estimatedTime }}</span>
          </div>
          <div v-if="order.delivery.rider" class="rider-info">
            <ImgComponent
              :src="order.delivery.rider.photo"
              :alt="order.delivery.rider.name"
              class="rider-photo"
            />
            <div class="rider-details">
              <h4>{{ order.delivery.rider.name }}</h4>
              <p>{{ order.delivery.rider.vehicle }}</p>
            </div>
            <div class="rider-actions">
              <a :href="`tel:${order.delivery.rider.phone}`" class="call-button">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </a>
              <button @click="openRiderChat" class="chat-button">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment Summary -->
      <div class="section payment-summary">
        <h2>Récapitulatif</h2>
        <div class="summary-row">
          <span>Sous-total</span>
          <span>{{ formatPrice(order.payment.subtotal) }}</span>
        </div>
        <div class="summary-row">
          <span>Frais de livraison</span>
          <div class="flex flex-col items-end">
            <span
              v-if="
                order.payment.originalDeliveryFee &&
                order.payment.originalDeliveryFee > order.payment.deliveryFee
              "
              class="text-xs text-gray-400 line-through"
            >
              {{ formatPrice(order.payment.originalDeliveryFee) }}
            </span>
            <span :class="{ 'text-green-600 font-bold': order.payment.deliveryPromotion?.discountAmount > 0 }">
              {{ formatPrice(order.payment.deliveryFee) }}
              <span v-if="order.payment.deliveryPromotion?.discountAmount > 0" class="text-[10px] bg-green-100 text-green-600 px-1 rounded ml-1">
                PROMO
              </span>
            </span>
          </div>
        </div>
        <div class="summary-row" v-if="order.payment.serviceFee">
          <span>Frais de service</span>
          <span>{{ formatPrice(order.payment.serviceFee) }}</span>
        </div>
        <div class="summary-row" v-if="order.payment.discount">
          <span>Remise</span>
          <span class="discount"
            >-{{ formatPrice(order.payment.discount) }}</span
          >
        </div>
        <div class="summary-row total">
          <span>Total</span>
          <span>{{ formatPrice(order.payment.total) }}</span>
        </div>
        <div class="payment-method">
          <span
            >Paiement par
            {{ getPaymentMethodLabel(order.payment.method) }}</span
          >
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="actions" v-if="order.canCancel">
        <button @click="cancelOrder" class="cancel-button">
          Annuler la commande
        </button>
      </div>
      <div class="actions" v-if="order.canRate">
        <button @click="rateOrder" class="rate-button">
          Noter la commande
        </button>
      </div>

      <!-- Track Button -->
      <button
        v-if="
          ['confirmed', 'preparing', 'ready', 'picked_up'].includes(
            order.status
          )
        "
        @click="goToTracking"
        class="track-order-button"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path
            d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <circle cx="12" cy="10" r="3" stroke-width="2" />
        </svg>
        Suivre la livraison
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const orderId = ref(route.params.id);

const order = computed(() => window.$stores?.orders?.state?.detail?.order);

const statusSteps = [
  { status: 'pending', label: 'En attente' },
  { status: 'confirmed', label: 'Confirmée' },
  { status: 'preparing', label: 'En préparation' },
  { status: 'ready', label: 'Prête' },
  { status: 'picked_up', label: 'En livraison' },
  { status: 'delivered', label: 'Livrée' }
];

const getStatusLabel = (status) => {
  const labels = {
    pending: 'En attente',
    confirmed: 'Confirmée',
    preparing: 'En préparation',
    ready: 'Prête',
    picked_up: 'En livraison',
    delivered: 'Livrée',
    cancelled: 'Annulée'
  };
  return labels[status] || status;
};

const getStatusClass = (status) => {
  const classes = {
    pending: 'status-pending',
    confirmed: 'status-confirmed',
    preparing: 'status-preparing',
    ready: 'status-ready',
    picked_up: 'status-delivering',
    delivered: 'status-delivered',
    cancelled: 'status-cancelled'
  };
  return classes[status] || '';
};

const isStepActive = (stepStatus) => {
  return order.value?.status === stepStatus;
};

const isStepCompleted = (stepStatus) => {
  const statusOrder = [
    'pending',
    'confirmed',
    'preparing',
    'ready',
    'picked_up',
    'delivered'
  ];
  const currentStatusIndex = statusOrder.indexOf(order.value?.status);
  const stepStatusIndex = statusOrder.indexOf(stepStatus);
  return stepStatusIndex < currentStatusIndex;
};

const getStepTime = (stepStatus) => {
  if (!order.value?.timestamps) return null;
  const timeMap = {
    pending: order.value.timestamps.createdAt,
    confirmed: order.value.timestamps.confirmedAt,
    preparing: order.value.timestamps.preparingAt,
    ready: order.value.timestamps.readyAt,
    picked_up: order.value.timestamps.pickedUpAt,
    delivered: order.value.timestamps.deliveredAt
  };
  const time = timeMap[stepStatus];
  if (!time) return null;
  return new Date(time).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'XOF',
    minimumFractionDigits: 0
  }).format(price);
};

const getPaymentMethodLabel = (method) => {
  const labels = {
    card: 'Carte bancaire',
    mobile_money: 'Mobile Money',
    cash: 'Espèces',
    wallet: 'Portefeuille'
  };
  return labels[method] || method;
};

const retry = () => {
  window.$bus?.emit(window.$constants?.EVENTS?.ORDERS?.DETAIL, {
    id: orderId.value
  });
};

const goBack = () => {
  window.$router?.back();
};

const goToRestaurant = (id) => {
  window.$router?.push(`/restaurants/${id}`);
};

const goToTracking = () => {
  window.$router?.push(`/orders/${orderId.value}/tracking`);
};

const openRiderChat = () => {
  window.$bus?.emit('chat.open_with_rider', order.value);
};

const cancelOrder = () => {
  if (confirm('Êtes-vous sûr de vouloir annuler cette commande ?')) {
    window.$bus?.emit(window.$constants?.EVENTS?.ORDERS?.CANCEL, {
      id: orderId.value
    });
  }
};

const rateOrder = () => {
  window.$bus?.emit(
    window.$constants?.EVENTS?.ORDERS?.REVIEW_REQUIRED || 'orders.review_required',
    order.value || { id: orderId.value }
  );
};
</script>

<style scoped>
.order-detail-page {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.loading,
.error {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #f59e0b;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error {
  color: #dc2626;
}

.retry-button {
  margin-top: 16px;
  padding: 10px 20px;
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}

.detail-header {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-weight: 500;
}

.detail-header h1 {
  font-size: 1.5em;
  font-weight: bold;
  color: #1a1a1a;
}

.order-status {
  align-self: flex-start;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.9em;
  font-weight: 600;
}

.status-pending {
  background: #fef3c7;
  color: #92400e;
}

.status-confirmed {
  background: #dbeafe;
  color: #1e40af;
}

.status-preparing {
  background: #e0e7ff;
  color: #3730a3;
}

.status-ready {
  background: #d1fae5;
  color: #065f46;
}

.status-delivering {
  background: #fce7f3;
  color: #9f1239;
}

.status-delivered {
  background: #d1fae5;
  color: #065f46;
}

.status-cancelled {
  background: #fee2e2;
  color: #991b1b;
}

.status-timeline {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.timeline-step {
  display: flex;
  position: relative;
  padding-bottom: 20px;
}

.timeline-step:last-child {
  padding-bottom: 0;
}

.step-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.timeline-step.active .step-icon {
  background: #f59e0b;
  color: white;
}

.timeline-step.completed .step-icon {
  background: #10b981;
  color: white;
}

.step-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: currentColor;
}

.step-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.step-label {
  font-weight: 600;
  color: #1a1a1a;
}

.step-time {
  font-size: 0.85em;
  color: #666;
}

.step-line {
  position: absolute;
  left: 15px;
  top: 32px;
  width: 2px;
  height: calc(100% - 12px);
  background: #f3f4f6;
}

.timeline-step.completed .step-line {
  background: #10b981;
}

.section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.section h2 {
  font-size: 1.2em;
  font-weight: 600;
  margin-bottom: 16px;
  color: #1a1a1a;
}

.restaurant-card {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 12px;
  border-radius: 8px;
  transition: background 0.2s;
}

.restaurant-card:hover {
  background: #f9fafb;
}

.restaurant-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
}

.restaurant-name {
  font-weight: 600;
  color: #1a1a1a;
}

.restaurant-address {
  font-size: 0.9em;
  color: #666;
  margin-top: 4px;
}

.arrow-icon {
  color: #9ca3af;
  margin-left: auto;
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.order-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
}

.order-item:last-child {
  border-bottom: none;
}

.item-info {
  display: flex;
  gap: 12px;
  flex: 1;
}

.item-quantity {
  font-weight: 600;
  color: #f59e0b;
  min-width: 30px;
}

.item-name {
  font-weight: 600;
  color: #1a1a1a;
}

.item-options {
  font-size: 0.85em;
  color: #666;
  margin-top: 4px;
}

.item-price {
  font-weight: 600;
  color: #1a1a1a;
}

.delivery-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.delivery-row {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #374151;
}

.delivery-row .icon {
  color: #f59e0b;
  flex-shrink: 0;
}

.rider-info {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  margin-top: 8px;
}

.rider-photo {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
}

.rider-details h4 {
  font-weight: 600;
  color: #1a1a1a;
}

.rider-details p {
  font-size: 0.9em;
  color: #666;
}

.rider-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.call-button,
.chat-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s;
}

.call-button:hover,
.chat-button:hover {
  opacity: 0.8;
}

.call-button {
  background: #f59e0b;
}

.chat-button {
  background: #3b82f6;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  color: #374151;
}

.summary-row.total {
  font-weight: 700;
  font-size: 1.1em;
  color: #1a1a1a;
  border-top: 2px solid #f3f4f6;
  margin-top: 8px;
  padding-top: 16px;
}

.summary-row.discount {
  color: #10b981;
}

.payment-method {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
  font-size: 0.9em;
  color: #666;
}

.actions {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.cancel-button,
.rate-button {
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

.cancel-button {
  background: #fee2e2;
  color: #991b1b;
}

.rate-button {
  background: #f59e0b;
  color: white;
}

.track-order-button {
  width: 100%;
  padding: 16px;
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.track-order-button:hover {
  background: #e5940b;
}
</style>
